import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import { libraryScansQuery, saveLibraryScans, type LibraryScansPage, type LibraryVerdict } from '../../api/settingsSoftsee'
import { deviceHref } from '../../devices/DeviceWorkspace'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings > Softsee > Library scans (auditul Ana Grup R1/R19): pornita, agentul
 * de Windows cauta o data pe zi, numai citire, log4j-core vulnerabil si log4j
 * 1.x pe disc. Verdictul il da ph4, pagina spune ce e de facut.
 */
export function LibraryScansPanel() {
  const page = useQuery(libraryScansQuery())

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) return <div className="alert alert-danger">{page.error.message}</div>
  if (!page.data) return <Spinner />

  return (
    <div id="content">
      <header className="panel-heading" style={{ borderBottom: 0 }}>
        <h3>
          <strong>Library</strong> scans
        </h3>
        <label className="color">a vulnerable Log4j found on the disk, before an attacker or an auditor finds it</label>
      </header>
      <Body page={page.data} />
    </div>
  )
}

function Body({ page }: { page: LibraryScansPage }) {
  if (!page.ready) {
    return <div className="alert alert-warning">The library scan tables are not installed yet. They come with the next database migration.</div>
  }
  if (page.company === null) {
    return <div className="alert alert-info">Pick one company in the header: library scans are switched on per company.</div>
  }

  return <ScanForm page={page} />
}

function plural(n: number, word: string) {
  return `${n.toLocaleString('en-US')} ${word}${n === 1 ? '' : 's'}`
}

const VERDICT: Record<LibraryVerdict, { label: string; className: string }> = {
  vulnerable: { label: 'Update to 2.17.1 or later', className: 'label label-danger' },
  'end-of-life': { label: 'Replace: log4j 1.x gets no fixes', className: 'label label-danger' },
  unknown: { label: 'Version not readable - check by hand', className: 'label label-warning' },
  fixed: { label: 'Fixed', className: 'label label-success' },
}

/** Intrarea din arhiva; pom.properties = Log4j impachetat intr-un fat jar, nu un fisier separat. */
function entryLabel(entry: string) {
  return entry.endsWith('/log4j-core/pom.properties') ? 'Log4j bundled inside' : entry
}

function ScanForm({ page }: { page: LibraryScansPage }) {
  const client = useQueryClient()
  const [enabled, setEnabled] = useState(page.enabled)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setEnabled(page.enabled)
  }, [page.enabled])

  const dirty = enabled !== page.enabled
  const summary = page.summary ?? { scanned: 0, flagged: 0 }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!page.canEdit || !dirty || busy) return
    setBusy(true)
    setError(null)
    try {
      const next = await saveLibraryScans(enabled)
      client.setQueryData(libraryScansQuery().queryKey, next)
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The setting could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="library-scans-form" noValidate onSubmit={(e) => void submit(e)}>
      <p>
        Company: <strong>{page.company?.name}</strong>
      </p>
      <p>
        {summary.scanned > 0
          ? `${plural(summary.scanned, 'machine')} scanned · ${summary.flagged} with a vulnerable or unsupported Log4j.`
          : page.enabled
            ? 'No machine has reported a scan yet. Each machine scans once a day, in the background; agents pick the setting up within an hour.'
            : 'Off: no machine is scanned.'}
      </p>
      <div className="checkbox">
        <label>
          <input
            type="checkbox"
            checked={enabled}
            disabled={!page.canEdit || busy}
            aria-describedby="library-scans-help"
            onChange={(e) => {
              setEnabled(e.target.checked)
              setSaved(false)
            }}
          />{' '}
          Search the machines for vulnerable Java libraries (Log4j) once a day
        </label>
      </div>
      <p id="library-scans-help" className="text-muted" style={{ fontSize: 12 }}>
        Read only, on workstations and Windows servers. The agent looks at file names and at the table of contents of .jar, .war and .ear archives -
        nothing is unpacked or changed. It searches Program Files, ProgramData and the local drives, skipping Windows, user profiles and network shares,
        and stops after 20 minutes or 400,000 files. It finds log4j-core before 2.17.1 (Log4Shell, CVE-2021-44228, and the fixes that followed) and
        log4j 1.x, which gets no fixes at all.
      </p>
      {error && <div className="alert alert-danger">{error}</div>}
      {saved && !error && <div className="alert alert-success">Saved.</div>}
      {page.canEdit && (
        <button type="submit" className="btn btn-primary" disabled={!dirty || busy} aria-label="Save library scans">
          {busy ? 'Saving...' : 'Save'}
        </button>
      )}
      <Findings page={page} />
      <Machines page={page} />
    </form>
  )
}

function Findings({ page }: { page: LibraryScansPage }) {
  if (page.findings.length === 0) return null

  return (
    <div style={{ marginTop: 16 }}>
      <p>
        <strong>Found on the machines</strong>
      </p>
      <div className="table-responsive">
        <table className="table table-condensed" aria-label="Found on the machines">
          <thead>
            <tr>
              {/* ce e de facut inaintea caii: pe telefon calea lunga o impingea afara din ecran */}
              <th>Machine</th>
              <th>What to do</th>
              <th>Library</th>
              <th>File</th>
            </tr>
          </thead>
          <tbody>
            {page.findings.map((f) => (
              <tr key={`${f.device_id}|${f.path}|${f.entry}`}>
                <td>
                  <a href={deviceHref(f.device_id, 'softsee')}>{f.name}</a>
                </td>
                <td>
                  <span className={VERDICT[f.verdict]?.className ?? 'label label-default'}>{VERDICT[f.verdict]?.label ?? f.verdict}</span>
                </td>
                <td>{`${f.library} ${f.version}`.trim()}</td>
                <td style={{ wordBreak: 'break-all' }}>
                  {f.path}
                  {f.entry !== '' && ` › ${entryLabel(f.entry)}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Machines({ page }: { page: LibraryScansPage }) {
  if (page.machines.length === 0) return null

  return (
    <div style={{ marginTop: 16 }}>
      <p>
        <strong>Last scan per machine</strong>
        {page.enabledAt && <span className="text-muted"> - on since {page.enabledAt.slice(0, 10)}</span>}
      </p>
      <div className="table-responsive">
        <table className="table table-condensed" aria-label="Last scan per machine">
          <thead>
            <tr>
              <th>Machine</th>
              <th>Scanned</th>
              <th>Files looked at</th>
              <th>Found</th>
            </tr>
          </thead>
          <tbody>
            {page.machines.map((m) => (
              <tr key={m.device_id}>
                <td>
                  <a href={deviceHref(m.device_id, 'softsee')}>{m.name}</a>
                </td>
                <td>{m.scanned_at.slice(0, 16)}</td>
                <td>
                  {m.files.toLocaleString('en-US')}
                  {m.truncated && <span className="text-muted"> - stopped at the limit, the rest was not looked at</span>}
                </td>
                <td>{m.findings}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
